import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const SchoolBuffetScenario = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedChoice, setSelectedChoice] = useState(null)

  const budget = 5.00

  // Available items
  const items = [
    { name: 'Bageta', price: 3.20, emoji: '🥖' },
    { name: 'Croissant', price: 1.80, emoji: '🥐' },
    { name: 'Džús', price: 1.50, emoji: '🧃' },
    { name: 'Voda', price: 1.00, emoji: '💧' },
    { name: 'Tyčinka', price: 0.90, emoji: '🍫' }
  ]

  // Predefined choices with consequences
  const choices = [
    {
      id: 1,
      name: 'Bageta + Džús',
      items: ['Bageta', 'Džús'],
      total: 4.70,
      left: 0.30,
      consequence: 'Kúpil si si veľkú bagetu a džús – zostalo ti iba 0,30 €. Si dosýta, ale už nemáš peniaze na desiatu zajtra.',
      icon: '🥖🧃'
    },
    {
      id: 2,
      name: 'Croissant + Voda + Tyčinka',
      items: ['Croissant', 'Voda', 'Tyčinka'],
      total: 3.70,
      left: 1.30,
      consequence: 'Kúpil si si menšie jedlo a vodu. Zostalo ti 1,30 €, čo môžeš ušetriť alebo použiť zajtra.',
      icon: '🥐💧🍫'
    },
    {
      id: 3,
      name: 'Tyčinka + Voda',
      items: ['Tyčinka', 'Voda'],
      total: 1.90,
      left: 3.10,
      consequence: 'Kúpil si si len malú tyčinku a vodu. Zostáva ti 3,10 € – budeš mať o chvíľu hlad, ale máš peniaze na neskôr.',
      icon: '🍫💧'
    }
  ]

  const tips = [
    {
      title: 'Plánuj vopred',
      text: 'Rozmysli si, či si dnes potrebuješ kúpiť všetko naraz, alebo môžeš časť peňazí ušetriť na zajtra.',
      emoji: '📅'
    },
    {
      title: 'Porovnávaj ceny',
      text: 'Aj malé rozdiely v cenách sa časom spočítajú. Voda stojí menej ako džús, ale tiež ťa osviežuje.',
      emoji: '💰'
    },
    {
      title: 'Mysli na dôsledky',
      text: 'Ak dnes miniem všetko, zajtra nebudem mať nič. Malé rozhodnutia majú veľké následky.',
      emoji: '🤔'
    }
  ]

  const handleChoiceSelect = (choice) => {
    setSelectedChoice(choice)
    setCurrentStep(7)
  }

  const handleRestart = () => {
    setCurrentStep(1)
    setSelectedChoice(null)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-green-100 mb-1">Interaktívny scenár</div>
                <h1 className="text-3xl font-bold">🏫 Školský bufet</h1>
              </div>
              {currentStep === 1 ? (
                <button
                  onClick={() => navigate('/financial-literacy')}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
                >
                  ← Späť na scenáre
                </button>
              ) : (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition text-sm font-medium"
                >
                  ← Späť
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2">
              {[...Array(13)].map((_, idx) => (
                <div
                  key={idx + 1}
                  className={`flex-1 h-2 rounded-full transition-all ${
                    idx + 1 <= currentStep ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-gray-600 mt-2 text-center">
              Krok {currentStep} z 13
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">

          {/* Intro Slide 1: Question */}
          {currentStep === 1 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="text-6xl mb-4">💭</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Čo je múdrejšie?
                </h2>
              </div>

              <div className="space-y-4 max-w-2xl mx-auto">
                <div className="p-6 bg-blue-50 rounded-xl border-2 border-blue-200">
                  <div className="font-bold text-gray-800 mb-2">A) Dnes si kúpim všetko, čo chcem</div>
                  <p className="text-sm text-gray-600">Mám hlad teraz, tak si kúpim veľa</p>
                </div>
                
                <div className="p-6 bg-green-50 rounded-xl border-2 border-green-200">
                  <div className="font-bold text-gray-800 mb-2">B) Ušetrím peniaze na zajtra</div>
                  <p className="text-sm text-gray-600">Možno zajtra budem chcieť niečo kúpiť</p>
                </div>
              </div>

              <div className="text-center mt-8">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition"
                >
                  Pokračovať
                </button>
              </div>
            </div>
          )}

          {/* Intro Slide 2: Discussion */}
          {currentStep === 2 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-6">
                <div className="text-5xl mb-4">💬</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Diskusia v triede
                </h2>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg max-w-2xl mx-auto">
                <h3 className="font-bold text-gray-900 mb-4">Zamyslite sa:</h3>
                <ul className="space-y-3 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-1">•</span>
                    <span>Čo sa stane, ak dnes miniem všetky peniaze?</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-1">•</span>
                    <span>Budem mať zajtra peniaze na desiatu?</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-1">•</span>
                    <span>Potrebujem si kúpiť to najdrahšie, alebo môžem ušetriť?</span>
                  </li>
                </ul>
              </div>

              <div className="text-center mt-8">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition"
                >
                  Pokračovať
                </button>
              </div>
            </div>
          )}

          {/* Intro Slide 3: Explanation */}
          {currentStep === 3 && !selectedChoice && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center">
              <div className="text-6xl mb-6">💡</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Rezerva a plánovanie
              </h2>
              <div className="max-w-2xl mx-auto">
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  Rezerva znamená mať peniaze na neskôr. Ak dnes miniem všetko, zajtra nebudem mať nič.
                </p>
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  Plánovanie pomáha myslieť dopredu – nemusím si dnes kúpiť to najdrahšie, môžem ušetriť a mať peniaze na celý týždeň.
                </p>
              </div>
              <button
                onClick={() => setCurrentStep(4)}
                className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition shadow-lg"
              >
                Začať scenár
              </button>
            </div>
          )}

          {/* Step 4: Introduction */}
          {currentStep === 4 && !selectedChoice && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center">
              <div className="text-6xl mb-6">🎒</div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Vitaj v školskom bufete!
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Je veľká prestávka a máš hlad. V peňaženke máš <strong className="text-green-600">5 €</strong>.
              </p>
              <p className="text-gray-600 mb-8">
                Čo si kúpiš? Tvoje rozhodnutie ovplyvní, koľko peňazí ti zostane na neskôr.
              </p>
              <button
                onClick={() => setCurrentStep(5)}
                className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition shadow-lg"
              >
                Začať scenár
              </button>
            </div>
          )}

          {/* Step 5: Menu items */}
          {currentStep === 5 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                Ponuka v bufete
              </h2>
              <p className="text-gray-600 mb-8 text-center">
                Máš k dispozícii: <span className="font-bold text-green-600 text-xl">5,00 €</span>
              </p>
              
              <div className="space-y-4 mb-8">
                {items.map((item) => (
                  <div
                    key={item.name}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border-2 border-gray-200"
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-4xl">{item.emoji}</span>
                      <span className="text-lg font-semibold text-gray-800">{item.name}</span>
                    </div>
                    <span className="text-xl font-bold text-green-600">
                      {item.price.toFixed(2)} €
                    </span>
                  </div>
                ))}
              </div>

              <div className="text-center">
                <button
                  onClick={() => setCurrentStep(6)}
                  className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition"
                >
                  Pokračovať na výber
                </button>
              </div>
            </div>
          )}

          {/* Step 6: Choose combination */}
          {currentStep === 6 && !selectedChoice && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                Čo si kúpiš?
              </h2>
              <p className="text-gray-600 mb-8 text-center">
                Vyber si jednu z možností
              </p>

              <div className="space-y-4">
                {choices.map((choice) => (
                  <button
                    key={choice.id}
                    onClick={() => handleChoiceSelect(choice)}
                    className="w-full p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border-2 border-green-200 hover:border-green-400 hover:shadow-lg transition-all text-left"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{choice.icon}</span>
                        <span className="text-lg font-bold text-gray-800">{choice.name}</span>
                      </div>
                      <span className="text-xl font-bold text-green-600">
                        {choice.total.toFixed(2)} €
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 ml-12">
                      Zostane ti: <span className="font-semibold text-green-600">{choice.left.toFixed(2)} €</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 7: Show result */}
          {currentStep === 7 && selectedChoice && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">{selectedChoice.icon}</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Tvoja voľba: {selectedChoice.name}
                </h2>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-2 gap-6 mb-4">
                  <div className="text-center">
                    <div className="text-sm text-gray-600 mb-1">Minul si</div>
                    <div className="text-3xl font-bold text-red-500">
                      {selectedChoice.total.toFixed(2)} €
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm text-gray-600 mb-1">Zostáva ti</div>
                    <div className="text-3xl font-bold text-green-600">
                      {selectedChoice.left.toFixed(2)} €
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mb-8">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">💡</span>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Čo to znamená?</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {selectedChoice.consequence}
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setCurrentStep(8)}
                  className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition"
                >
                  Zobraziť tipy
                </button>
              </div>
            </div>
          )}

          {/* Step 8: Show tips */}
          {currentStep === 8 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                📚 Tipy na lepšie rozhodovanie
              </h2>
              <p className="text-gray-600 mb-8 text-center">
                Zapamätaj si tieto jednoduché pravidlá
              </p>

              <div className="space-y-4 mb-8">
                {tips.map((tip, index) => (
                  <div
                    key={index}
                    className="p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border border-green-200"
                  >
                    <div className="flex items-start gap-4">
                      <span className="text-3xl">{tip.emoji}</span>
                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">{tip.title}</h3>
                        <p className="text-gray-700 text-sm leading-relaxed">{tip.text}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center">
                <button
                  onClick={() => setCurrentStep(9)}
                  className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition"
                >
                  Pokračovať
                </button>
              </div>
            </div>
          )}

          {/* Step 9: Next day scenario */}
          {currentStep === 9 && selectedChoice && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">🌅</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Ďalší deň v škole
                </h2>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mb-6">
                <p className="text-gray-700 leading-relaxed mb-3">
                  Je to ďalší deň. <strong>Rodičia ti dali opäť 5,00 € vreckové.</strong>
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Včera ti zostalo <strong className="text-green-600">{selectedChoice.left.toFixed(2)} €</strong>, takže teraz máš celkovo <strong className="text-green-600">{(5.00 + selectedChoice.left).toFixed(2)} €</strong>.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Opäť máš prestávku a opäť máš hlad. <strong>Rozhodneš sa kúpiť to isté ako včera</strong> ({selectedChoice.name}).
                </p>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setCurrentStep(10)}
                  className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition"
                >
                  Pokračovať
                </button>
              </div>
            </div>
          )}

          {/* Step 10: Calculate savings */}
          {currentStep === 10 && selectedChoice && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                📊 Kalkulácia po 2 dňoch
              </h2>

              <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 mb-6">
                <div className="space-y-4 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Prvý deň - dostal si:</span>
                    <span className="font-bold text-blue-600">5,00 €</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Prvý deň - minul si:</span>
                    <span className="font-bold text-red-500">-{selectedChoice.total.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Prvý deň - zostalo:</span>
                    <span className="font-bold text-green-600">{selectedChoice.left.toFixed(2)} €</span>
                  </div>
                  
                  <div className="border-t border-gray-300 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Druhý deň - dostal si:</span>
                      <span className="font-bold text-blue-600">5,00 €</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Druhý deň - minul si:</span>
                      <span className="font-bold text-red-500">-{selectedChoice.total.toFixed(2)} €</span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t-2 border-gray-400 text-center">
                  <div className="text-sm text-gray-600 mb-1">Celkovo ti zostáva</div>
                  <div className={`text-4xl font-bold ${
                    (10 - selectedChoice.total * 2) >= 2 ? 'text-green-600' : 'text-orange-500'
                  }`}>
                    {(10 - selectedChoice.total * 2).toFixed(2)} €
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    (Dostal si 10,00 €, minul si {(selectedChoice.total * 2).toFixed(2)} €)
                  </div>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setCurrentStep(11)}
                  className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition"
                >
                  Pokračovať
                </button>
              </div>
            </div>
          )}

          {/* Step 11: Unexpected situation */}
          {currentStep === 11 && selectedChoice && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">😮</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Nečakaná situácia!
                </h2>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Tvoj kamarát zabudol peniaze doma a nemá si čo kúpiť na desiatu. Pýta si od teba 1,50 € na croissant a vodu.
                </p>
                <p className="text-gray-700 font-medium">
                  Ty máš teraz <strong className="text-green-600">{(10 - selectedChoice.total * 2).toFixed(2)} €</strong>. Čo urobíš?
                </p>
              </div>

              {(10 - selectedChoice.total * 2) >= 1.50 ? (
                <div className="space-y-4">
                  <button
                    onClick={() => {
                      setSelectedChoice({...selectedChoice, helpedFriend: true})
                      setCurrentStep(12)
                    }}
                    className="w-full p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl border-2 border-green-200 hover:border-green-400 hover:shadow-lg transition-all text-left"
                  >
                    <div className="font-bold text-gray-800 mb-1">
                      Požičiať kamarátovi 1,50 €
                    </div>
                    <div className="text-sm text-gray-600">
                      Pomôžeš kamarátovi, ale ti zostane menej peňazí ({((10 - selectedChoice.total * 2) - 1.50).toFixed(2)} €)
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setSelectedChoice({...selectedChoice, helpedFriend: false})
                      setCurrentStep(12)
                    }}
                    className="w-full p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border-2 border-orange-200 hover:border-orange-400 hover:shadow-lg transition-all text-left"
                  >
                    <div className="font-bold text-gray-800 mb-1">
                      Nechať si peniaze
                    </div>
                    <div className="text-sm text-gray-600">
                      Sporiacky rozumné, ale kamarát bude hladný
                    </div>
                  </button>
                </div>
              ) : (
                <div>
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg mb-6">
                    <p className="text-gray-700 leading-relaxed">
                      <strong>Nemáš dosť peňazí.</strong> Potrebuješ 1,50 €, ale máš len {(10 - selectedChoice.total * 2).toFixed(2)} €. 
                      Rád by si pomohol, ale jednoducho nemôžeš – minul si príliš veľa v predošlých dňoch.
                    </p>
                  </div>
                  <div className="text-center">
                    <button
                      onClick={() => {
                        setSelectedChoice({...selectedChoice, helpedFriend: false, notEnoughMoney: true})
                        setCurrentStep(12)
                      }}
                      className="px-8 py-4 bg-orange-600 text-white rounded-xl font-bold text-lg hover:bg-orange-700 transition"
                    >
                      Pokračovať
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 12: Final reflection */}
          {currentStep === 12 && selectedChoice && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                💭 Čo si sa naučil?
              </h2>

              <div className="space-y-4 mb-8">
                <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6">
                  <h3 className="font-bold text-gray-900 mb-2">Tvoje rozhodnutia:</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• Prvý deň: {selectedChoice.name} (minul si {selectedChoice.total.toFixed(2)} €, zostalo {selectedChoice.left.toFixed(2)} €)</li>
                    <li>• Druhý deň: To isté (minul si {selectedChoice.total.toFixed(2)} €)</li>
                    <li>• Po 2 dňoch: Zostalo ti {(10 - selectedChoice.total * 2).toFixed(2)} €</li>
                    <li>• Kamarát: {selectedChoice.notEnoughMoney 
                      ? 'Nemohol si požičať - nemal si dosť peňazí' 
                      : selectedChoice.helpedFriend 
                        ? 'Požičal si 1,50 €' 
                        : 'Nechcel si požičať'
                    }</li>
                    <li>• Nakoniec ti zostáva: {selectedChoice.notEnoughMoney || !selectedChoice.helpedFriend
                      ? (10 - selectedChoice.total * 2).toFixed(2) 
                      : ((10 - selectedChoice.total * 2) - 1.50).toFixed(2)
                    } €</li>
                  </ul>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    <strong>Poučenie:</strong> {selectedChoice.notEnoughMoney
                      ? 'Ak minieme príliš veľa na začiatku, neskôr nemáme peniaze na dôležité veci – ani keď chceme pomôcť kamarátovi. Preto je dôležité šetriť a plánovať vopred.'
                      : selectedChoice.helpedFriend 
                        ? 'Ukázal si, že vieš pomôcť ostatným, aj keď to pre teba znamená mať menej peňazí. To je dôležité – niekedy sú vzťahy dôležitejšie ako peniaze.'
                        : 'Rozhodol si sa šetriť svoje peniaze. To je tiež v poriadku – musíš sa starať o svoje potreby. Dôležité je myslieť na rovnováhu medzi šetrením a pomocou ostatným.'
                    }
                  </p>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setCurrentStep(13)}
                  className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition"
                >
                  Dokončiť scenár
                </button>
              </div>
            </div>
          )}

          {/* Step 10: Completion */}
          {currentStep === 13 && (
            <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center">
              <div className="text-6xl mb-6">🎉</div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Scenár dokončený!
              </h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Skvelá práca! Naučil si sa, ako malé rozhodnutia ovplyvňujú tvoje peniaze.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleRestart}
                  className="px-8 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition shadow-lg"
                >
                  🔄 Skúsiť znova
                </button>
                <button
                  onClick={() => navigate('/scenarios')}
                  className="px-8 py-4 bg-gray-100 text-gray-700 rounded-xl font-bold text-lg hover:bg-gray-200 transition"
                >
                  ← Späť na ročníky
                </button>
              </div>

              <div className="mt-8 p-6 bg-blue-50 rounded-xl">
                <p className="text-sm text-gray-700">
                  <strong>Ďalší scenár:</strong> Vyskúšaj scenár "Mobil a kredity" pre ďalšie finančné rozhodovanie!
                </p>
              </div>
            </div>
          )}

        </div>
      </div>

    </div>
  )
}

export default SchoolBuffetScenario
